import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import type { SoverdiSectorView } from '../../soverdi-clone/adapters/agriSafeDashboardAdapter';
import type { CgmValveControl } from './valveControlPresentation';
import type { ParcelValveBinding } from '../../lib/iotApi';
import { scopeAlerts } from '../../soverdi-clone/adapters/dashboardContext';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { Activity, Droplets, Power, Star, Sun, Waves } from 'lucide-react';
import PumpActuatorControls from '../../components/PumpActuatorControls';
import ParcelSupervision from '../../components/ParcelSupervision';
import CgmPressureStatus from '../../components/CgmPressureStatus';

function preference(key: string, fallback: unknown) {
  try { return JSON.parse(window.localStorage.getItem(key) ?? 'null') ?? fallback; } catch { return fallback; }
}
function savePreference(key: string, value: unknown) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* Storage may be unavailable. */ }
}

export type HomeParcelView = SoverdiSectorView & { bindingId: string };

/** Names and identities come from the canonical operational bindings, never sector aggregation. */
export function homeParcelView(binding: ParcelValveBinding): HomeParcelView {
  return {
    id: binding.sector_id, bindingId: binding.binding_id,
    name: binding.parcel, displayName: binding.parcel,
    canonicalParcelNames: [binding.parcel], canonicalDisplayName: binding.parcel,
    customDisplayName: null, siteName: binding.site, sectorCode: binding.sector_code,
    status: 'UNKNOWN', operationalState: 'unknown', valveState: 'unknown', stateSinceMs: null,
    pressureBar: null, pressureFreshness: 'absent', flowM3h: null, flowFreshness: 'absent',
    soilMoisturePercent: null, soilMoistureFreshness: 'absent',
    volumeM3: null, remainingMinutes: null, progressPercent: null,
  };
}

interface Props {
  siteName: string;
  dashboard: AgriSafeSoverdiDashboard;
  resolveValveControl: (parcel: SoverdiSectorView) => CgmValveControl | null;
}

const CgmHomeParcelsR1: React.FC<Props> = ({ siteName, dashboard, resolveValveControl }) => {
  const { t } = useTranslation(['agrisafeR1', 'common']);
  const displayName = (binding: ParcelValveBinding) => dashboard.parcelDisplayName?.(binding) ?? binding.parcel;
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('');
  const pressureBindings = useFieldBindings();
  const pressureNode = (binding: ParcelValveBinding) => {
    if (!dashboard.nodesLoaded || pressureBindings.error || !pressureBindings.loaded
      || pressureBindings.farmId !== dashboard.topology?.farmId) return undefined;
    const id = pressureBindings.pressureSensorByBinding?.[binding.binding_id];
    const matches = typeof id === 'string' ? dashboard.nodes.filter((node) => node.id === id) : [];
    return matches.length === 1 ? matches[0] : undefined;
  };
  const [sunlight, setSunlight] = useState(() => preference('agrisafe-cgm-sunlight-mode', false) === true);
  const [favorites, setFavorites] = useState<string[]>(() => {
    const value = preference('agrisafe-cgm-favorite-sectors', []);
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
  });
  const bindings = dashboard.bindingsUnavailable ? [] : dashboard.parcelBindings
    .filter((binding) => binding.site === siteName && binding.binding_status === 'CONFIRMED');
  const selected = bindings.find((binding) => binding.binding_id === selectedId) ?? bindings[0];
  const rootClass = ['r53-standard-dashboard space-y-5 pb-40', sunlight && 'r53-sunlight'].filter(Boolean).join(' ');
  const detailPath = '/sectors/' + encodeURIComponent([selected?.site, selected?.sector_code].join(':')) + '?binding=' + encodeURIComponent(selected?.binding_id ?? '');
  const selectedMetrics = selected && bindings.filter((b) => b.sector_id === selected.sector_id).length === 1
    ? dashboard.sectors?.find((sector) => sector.siteName === selected.site && sector.sectorCode === selected.sector_code) : undefined;
  const control = selected ? resolveValveControl(homeParcelView(selected)) : null;
  const isFavorite = (binding: ParcelValveBinding) => favorites.includes(binding.binding_id)
    || favorites.includes(`${binding.site}:${binding.sector_code}`);
  const sortedBindings = [...bindings].sort((a, b) => Number(isFavorite(b)) - Number(isFavorite(a)));
  const pump = dashboard.siteModels?.find((site) => site.siteName === siteName);
  const pumpNodes = pressureBindings.loaded && !pressureBindings.error
    && pressureBindings.farmId === dashboard.topology?.farmId && dashboard.nodesLoaded
    ? dashboard.nodes.filter((node) => (pressureBindings.pumps ?? []).some((binding) => binding.site === siteName && binding.relay_node_id === node.id)) : [];
  const alertsUnavailable = dashboard.view?.alertsUnavailable || dashboard.placementUnavailable;
  const alerts = alertsUnavailable ? [] : scopeAlerts({
    alerts: dashboard.view?.alerts ?? [], nodes: dashboard.nodes ?? [],
    placement: dashboard.placement ?? new Map(), siteName,
  }).filter((item) => item.scope !== 'otherSite' && !item.alert.acknowledged)
    .sort((a, b) => Number(b.alert.severity === 'critical') - Number(a.alert.severity === 'critical'));
  const stateLabel = (state: string | undefined, powerSavingPossible = false) => powerSavingPossible
    ? t('agrisafeR1:parcel.state.powerSavingPossible')
    : state === 'open'
    ? t('agrisafeR1:parcel.state.open')
    : state === 'closed' ? t('agrisafeR1:parcel.state.closed') : t('agrisafeR1:dashboard.state.unknown');

  if (dashboard.loading) return <p role="status">{t('common:loading')}</p>;
  if (dashboard.loadError || dashboard.bindingsUnavailable) {
    return <p role="alert">{t('agrisafeR1:dashboard.dataUnavailable')}</p>;
  }

  return (
    <div className={rootClass} data-testid="cgm-home-parcels-r1">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-[var(--text-primary)]">{dashboard.view?.farmName}</h1>
        <button type="button" className="r53-field-button min-h-[44px] rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 flex items-center gap-2 text-sm font-semibold" aria-pressed={sunlight}
          onClick={() => { setSunlight(!sunlight); savePreference('agrisafe-cgm-sunlight-mode', !sunlight); }}>
          <Sun className="w-4 h-4" aria-hidden="true" />{t('common:cgmHomeSunlight')}
        </button>
      </div>
      {selected && (
        <section className="r53-hero rounded-2xl p-4 sm:p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--color-primary-700), var(--color-primary-400))' }} aria-label={displayName(selected)}>
          <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full bg-white/10" aria-hidden="true" />
          <div className="relative z-10 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
              <div>
                <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('agrisafeR1:dashboard.selectedSector')}</p>
                <h2 className="text-xl sm:text-3xl font-semibold leading-tight">{displayName(selected)}</h2>
                <p className="text-sm text-white/80 mt-1">{stateLabel(control?.valveState, control?.powerSavingPossible)}</p>
              </div>
              <div className="rounded-xl bg-black/15 px-4 py-3 min-w-[220px]">
                <CgmPressureStatus key={selected.binding_id} node={pressureNode(selected)} valveState={control?.valveState} detailed variant="summary" />
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
              <HeroMetric icon={<Power className="w-4 h-4" />} label={t('common:cgmHomeValveLabel')} value={stateLabel(control?.valveState, control?.powerSavingPossible)} />
              <HeroMetric icon={<Droplets className="w-4 h-4" />} label={t('agrisafeR1:dashboard.metrics.soilMoisture')} value={`${selectedMetrics?.soilMoistureFreshness === 'fresh' ? selectedMetrics.soilMoisturePercent : '—'} %`} />
              <HeroMetric icon={<Waves className="w-4 h-4" />} label={t('agrisafeR1:dashboard.metrics.flow')} value={`${selectedMetrics?.flowFreshness === 'fresh' ? selectedMetrics.flowM3h : '—'} m³/h`} />
              <CgmPressureStatus node={pressureNode(selected)} valveState={control?.valveState} variant="metrics" />
            </div>
            {(control?.availabilityText || control?.statusText) && (
              <p role="status" className="rounded-xl bg-white/15 border border-white/20 px-4 py-3 text-sm font-medium" data-testid="dashboard-valve-command-status">{control.statusText ?? control.availabilityText}</p>
            )}
            <ParcelSupervision data={(dashboard.nodes ?? []).find(node => node.id === selected.controller_node_id)?.parcel_supervision?.[selected.binding_id]} />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              <button type="button" className="r53-field-button min-h-[56px] rounded-xl bg-emerald-500 text-white font-semibold text-base disabled:bg-white/15 disabled:text-white/45 disabled:cursor-not-allowed"
                disabled={!control?.canOpen} onClick={control?.onOpen}>{t('agrisafeR1:parcel.open')}</button>
              <button type="button" className="r53-field-button min-h-[56px] rounded-xl bg-red-500 text-white font-semibold text-base disabled:bg-white/15 disabled:text-white/45 disabled:cursor-not-allowed"
                disabled={!control?.canClose} onClick={control?.onClose}>{t('agrisafeR1:parcel.close')}</button>
              <button type="button" className="r53-field-button col-span-2 lg:col-span-1 min-h-[56px] rounded-xl border border-white/30 bg-white/10 hover:bg-white/20 font-semibold flex items-center justify-center gap-2" onClick={() => navigate(detailPath)}><Activity className="w-4 h-4" aria-hidden="true" />{t('agrisafeR1:dashboard.detail')}</button>
            </div>
          </div>
        </section>
      )}
      <section>
        <h2 className="mb-3 font-semibold">{t('agrisafeR1:equipment.parcels')} ({bindings.length})</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {sortedBindings.map((binding) => {
            const parcelControl = resolveValveControl(homeParcelView(binding));
            return (
              <article key={binding.binding_id} className={`relative text-left bg-[var(--surface)] border-2 rounded-xl p-3 transition-all min-h-[152px] ${binding.binding_id === selected?.binding_id ? 'border-[var(--color-primary)] shadow-md' : 'border-[var(--border-light)]'}`}>
              <button type="button" data-testid="home-parcel-card"
                aria-pressed={binding.binding_id === selected?.binding_id}
                className="w-full min-h-[64px] text-left pr-8"
                onClick={() => setSelectedId(binding.binding_id)}>
                <h3 className="font-semibold text-sm text-[var(--text-primary)] leading-tight">{displayName(binding)}</h3>
                <p className="text-[10px] text-[var(--text-secondary)] mt-2">{stateLabel(parcelControl?.valveState, parcelControl?.powerSavingPossible)}</p>
              </button>
              <CgmPressureStatus node={pressureNode(binding)} valveState={parcelControl?.valveState} />
              <button type="button" className="min-h-[44px] px-2 text-xs text-[var(--color-primary)]" onClick={() => navigate('/sectors/' + encodeURIComponent(`${binding.site}:${binding.sector_code}`) + '?binding=' + encodeURIComponent(binding.binding_id))}>
                {t('agrisafeR1:dashboard.detail')}
              </button>
              <button type="button" className="r53-field-button absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-tertiary)] aria-pressed:text-[var(--color-warning)]" aria-pressed={isFavorite(binding)}
                aria-label={`${t('common:cgmHomeFavorite')} ${displayName(binding)}`} onClick={() => {
                  const legacy = `${binding.site}:${binding.sector_code}`;
                  // Expand legacy grouped favorites before changing this one parcel.
                  const expanded = new Set(favorites.flatMap((id) => id === legacy
                    ? bindings.filter((item) => `${item.site}:${item.sector_code}` === legacy).map((item) => item.binding_id) : [id]));
                  if (isFavorite(binding)) expanded.delete(binding.binding_id); else expanded.add(binding.binding_id);
                  const next = Array.from(expanded); setFavorites(next); savePreference('agrisafe-cgm-favorite-sectors', next);
                }}><Star className={`w-4 h-4 ${[isFavorite(binding) && 'fill-current'].filter(Boolean).join(' ')}`} aria-hidden="true" /></button>
              </article>
            );
          })}
        </div>
        {bindings.length === 0 && <p>{t('common:noSectors')}</p>}
      </section>
      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 space-y-3">
        <h2 className="font-semibold text-[var(--text-primary)]">{t('agrisafeR1:nav.pumping')}</h2>
        <p>{pump?.operationalState === 'active' ? t('agrisafeR1:dashboard.state.active')
          : pump?.operationalState === 'stopped' ? t('agrisafeR1:dashboard.state.stopped')
          : pump?.operationalState === 'not_commissioned' ? t('common:nonCommissioned') : t('agrisafeR1:dashboard.state.unknown')}</p>
        {pumpNodes.map((node) => <PumpActuatorControls key={node.id} node={node} siteName={siteName} />)}
      </section>
      <section className="rounded-xl border p-4">
        <h2>{t('common:alerts')} ({alertsUnavailable ? '—' : alerts.length})</h2>
        {alertsUnavailable && <p role="status">{t('agrisafeR1:dashboard.dataUnavailable')}</p>}
        {alerts.slice(0, 3).map(({ alert }) => <p key={alert.id}>{alert.message}</p>)}
      </section>
      <nav className="flex flex-wrap gap-3">
        <button type="button" className="min-h-[44px] rounded-lg border px-4" onClick={() => navigate('/sectors')}>{t('agrisafeR1:nav.sectors')}</button>
        <button type="button" className="min-h-[44px] rounded-lg border px-4" onClick={() => navigate('/pumping')}>{t('agrisafeR1:nav.pumping')}</button>
        <button type="button" className="min-h-[44px] rounded-lg border px-4" onClick={() => navigate('/alerts')}>{t('common:alerts')}</button>
      </nav>
      {selected && <div className="r53-mobile-actionbar fixed inset-x-0 bottom-[68px] z-40 rounded-xl border bg-[var(--surface)] p-2 lg:hidden flex justify-between items-center">
        <div><span>{displayName(selected)}</span><p role="status">{control?.statusText ?? control?.availabilityText}</p></div>
        <button type="button" className="r53-field-button min-h-[46px] px-4" disabled={!control?.canClose} onClick={control?.onClose}>
          {t('agrisafeR1:parcel.close')}
        </button>
        <button type="button" className="r53-field-button min-h-[46px] px-4" onClick={() => navigate(detailPath)}>
          {t('agrisafeR1:dashboard.detail')}
        </button>
      </div>}
    </div>
  );
};

const HeroMetric: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="rounded-xl bg-white/15 p-3 min-h-[74px]">
    <div className="flex items-center gap-1.5 text-xs text-white/70 mb-1">{icon}<span>{label}</span></div>
    <div className="font-semibold text-base sm:text-lg">{value}</div>
  </div>
);

export default CgmHomeParcelsR1;
